import { useState, useRef, useMemo } from 'react';
import SensorMap from '../Soil/SensorMap';
import {
  DEMO_WEATHER_STATIONS,
  WEATHER_PARAM_CONFIG,
  getDemoWeatherParamHistoryRange,
  WeatherParamKey,
} from '../../data/demo';
import { useWeatherThresholds } from '../../hooks/useWeatherThresholds';
import { useWeatherStations } from '../../hooks/useWeatherStations';
import { useTranslation } from 'react-i18next';
import WeatherMap from './WeatherMap';
import { useUbicacionClima } from '../../hooks/useUbicacionClima';

function fmt(v: number | undefined, suffix: string): string {
  return v != null ? `${v}${suffix}` : '—';
}

function getStatus(value: number, lo: number, hi: number): 'good' | 'warn' | 'alert' {
  const margin = (hi - lo) * 0.15;
  if (value < lo - margin || value > hi + margin) return 'alert';
  if (value < lo || value > hi) return 'warn';
  return 'good';
}

const STATUS_COLOR: Record<string, string> = {
  good: 'var(--green)',
  warn: 'var(--amber)',
  alert: 'var(--red)',
};

function toLocalDatetime(ms: number) {
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

// ── Page ──────────────────────────────────────────────────────

export default function WeatherPage() {
  const { thresholds } = useWeatherThresholds();
  const { stations, isDemo } = useWeatherStations();
  const { t: tWeather } = useTranslation();
  // El mapa del clima usa la ubicación que el cliente fijó en
  // Configuración; si no fijó ninguna, el promedio de sus estaciones.
  const { ubicacion: ubicacionClima } = useUbicacionClima(stations);
  const [selectedStationId, setSelectedStationId] = useState<string | null>(null);
  const [selectedParam, setSelectedParam] = useState<WeatherParamKey>('temperature');
  const now = useRef(Date.now()).current;
  const [startMs, setStartMs] = useState(now - 7 * 24 * 3_600_000);
  const [endMs, setEndMs] = useState(now);
  const historyRef = useRef<HTMLDivElement>(null);

  // null = ninguna estación reportó este parámetro todavía — se muestra
  // "N/A", nunca 0 (0 se vería como un dato real, ver useWeatherStations.ts).
  const averages = useMemo(() => {
    const result: Record<string, number | null> = {};
    for (const p of WEATHER_PARAM_CONFIG) {
      const vals = stations
        .map(s => (s.current as unknown as Record<string, number | undefined>)[p.key])
        .filter((v): v is number => v != null);
      result[p.key] = vals.length > 0
        ? Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 10) / 10
        : null;
    }
    return result;
  }, [stations]);

  // Sin fuente predictiva real — solo tiene sentido con datos demo,
  // siempre calculado desde DEMO_WEATHER_STATIONS (ver render: gateado a
  // isDemo, jamás se muestra junto a condiciones actuales reales).
  const avgForecast = useMemo(() => {
    const ref = DEMO_WEATHER_STATIONS[0].forecast;
    return ref.map((f, i) => {
      const allTemps = DEMO_WEATHER_STATIONS.map(s => s.forecast[i]?.temp ?? f.temp);
      const allRains = DEMO_WEATHER_STATIONS.map(s => s.forecast[i]?.rain ?? f.rain);
      return {
        label: f.label, icon: f.icon,
        temp: Math.round(allTemps.reduce((a, b) => a + b, 0) / allTemps.length),
        rain: Math.round(allRains.reduce((a, b) => a + b, 0) / allRains.length),
      };
    });
  }, []);

  const mapCenter: [number, number] = useMemo(() => {
    if (stations.length === 0) return [0, 0];
    return [
      stations.reduce((a, s) => a + s.lat, 0) / stations.length,
      stations.reduce((a, s) => a + s.lng, 0) / stations.length,
    ];
  }, [stations]);

  const mapMarkers = useMemo(() => stations.map(s => ({
    id: s.id, lat: s.lat, lng: s.lng,
    status: s.status as 'good' | 'warn' | 'alert',
    label: 'WS',
  })), [stations]);

  const selectedStation = stations.find(s => s.id === selectedStationId) ?? null;

  const historyData = useMemo(() => {
    // getDemoWeatherParamHistoryRange busca la estación por imei dentro de
    // DEMO_WEATHER_STATIONS — para un imei real no la encuentra y cae a un
    // `base` hardcodeado, generando un gráfico con pinta real pero
    // totalmente falso. No llamarla fuera de isDemo (ver render: placeholder
    // "próximamente" para estaciones reales).
    if (!selectedStation || !isDemo) return [];
    return getDemoWeatherParamHistoryRange(selectedStation.imei, selectedParam, startMs, endMs);
  }, [selectedStation, selectedParam, startMs, endMs, isDemo]);

  const handleStationSelect = (id: string) => {
    setSelectedStationId(id);
    setTimeout(() => historyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
  };

  return (
    <div className="page" style={{ overflowY: 'auto' }}>

      {/* ── 1. General Conditions ──────────────────────────── */}
      <SectionLabel>{tWeather('weatherPage.generalConditions')}</SectionLabel>
      <div style={{ fontSize: 12, color: 'var(--gray)', marginBottom: 16 }}>
        Averaged across {stations.length} active station{stations.length === 1 ? '' : 's'}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 40 }}>
        {WEATHER_PARAM_CONFIG.map(p => {
          const value = averages[p.key];
          if (value == null) {
            return (
              <div key={p.key} style={{
                background: 'var(--white)', border: '1px solid var(--border)',
                borderRadius: 12, padding: '14px 16px',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 10 }}>
                  <span style={{ fontSize: 20 }}>{p.icon}</span>
                  <span style={{ fontSize: 12, color: 'var(--gray)', fontWeight: 500 }}>{p.label}</span>
                </div>
                <div style={{ fontSize: 30, fontWeight: 700, color: 'var(--gray)', lineHeight: 1 }}>N/A</div>
                <div style={{ fontSize: 10, color: 'var(--gray)', marginTop: 2 }}>no data yet</div>
              </div>
            );
          }
          const thr = thresholds[p.key as WeatherParamKey] ?? { lo: p.lo, hi: p.hi };
          const status = getStatus(value, thr.lo, thr.hi);
          const color = STATUS_COLOR[status];
          const pct = Math.min(100, Math.max(0, ((value - p.lo) / (p.hi - p.lo)) * 100));
          return (
            <div key={p.key} style={{
              background: 'var(--white)', border: '1px solid var(--border)',
              borderRadius: 12, padding: '14px 16px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 10 }}>
                <span style={{ fontSize: 20 }}>{p.icon}</span>
                <span style={{ fontSize: 12, color: 'var(--gray)', fontWeight: 500 }}>{p.label}</span>
              </div>
              <div style={{ fontSize: 30, fontWeight: 700, color, lineHeight: 1 }}>{value}</div>
              <div style={{ fontSize: 10, color: 'var(--gray)', marginTop: 2, marginBottom: 10 }}>{p.unit}</div>
              <div style={{ height: 5, background: 'var(--border)', borderRadius: 3, overflow: 'hidden', marginBottom: 6 }}>
                <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 3, transition: 'width 0.3s' }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10 }}>
                <span style={{ color: 'var(--gray)' }}>{p.lo}</span>
                <span style={{ color, fontWeight: 600 }}>
                  {status === 'good' ? 'Optimal' : status === 'warn' ? 'Warning' : 'Alert'}
                </span>
                <span style={{ color: 'var(--gray)' }}>{p.hi} {p.unit}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── 2. Forecast ────────────────────────────────────── */}
      <SectionLabel>32-Hour Forecast</SectionLabel>
      <div style={{ fontSize: 12, color: 'var(--gray)', marginBottom: 16 }}>
        {tWeather('weatherPage.combinedOutlook')}
      </div>
      {!isDemo ? (
        <div style={{
          background: 'var(--bg)', border: '1px dashed var(--border)',
          borderRadius: 12, padding: '24px', textAlign: 'center',
          color: 'var(--gray)', fontSize: 13, marginBottom: 40,
        }}>
          Forecast requires a predictive data source — not available yet for connected stations.
        </div>
      ) : (
        <div style={{
          display: 'flex', overflowX: 'auto',
          background: 'var(--white)', borderRadius: 12, border: '1px solid var(--border)',
          marginBottom: 40,
        }}>
          {avgForecast.map((f, i) => (
            <div key={i} style={{
              flex: '1 0 auto', textAlign: 'center', padding: '14px 10px',
              borderRight: i < avgForecast.length - 1 ? '1px solid var(--border)' : 'none',
            }}>
              <div style={{ fontSize: 11, color: 'var(--gray)', marginBottom: 8 }}>{f.label}</div>
              <div style={{ fontSize: 26, marginBottom: 8 }}>{f.icon}</div>
              <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--dark)', marginBottom: 4 }}>{f.temp}°</div>
              <div style={{ fontSize: 10, color: f.rain > 40 ? '#1d4ed8' : 'var(--gray)' }}>💧 {f.rain}%</div>
            </div>
          ))}
        </div>
      )}

      {/* ── Mapa del clima ─────────────────────────────────────
          Separado del mapa de estaciones que va abajo. Éste contesta "qué
          tiempo viene" —radar, nubes de tormenta y ciclones—; el de abajo
          contesta "cómo van mis equipos". Tenerlos juntos hacía que la
          lluvia animada tapara los marcadores justo cuando urgía leerlos. */}
      <SectionLabel>{tWeather('weather.mapTitle')}</SectionLabel>
      <div style={{ height: 380, borderRadius: 12, overflow: 'hidden', border: '1px solid var(--border)', marginBottom: 40 }}>
        {ubicacionClima ? (
          <WeatherMap lat={ubicacionClima.lat} lng={ubicacionClima.lng} nombre={ubicacionClima.nombre || undefined} />
        ) : (
          <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 6, background: 'var(--bg)' }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--dark)' }}>{tWeather('weather.noLocation')}</span>
            <span style={{ fontSize: 12, color: 'var(--gray)' }}>{tWeather('weather.noLocationHint')}</span>
          </div>
        )}
      </div>

      {/* ── 3. Station Overview ────────────────────────────── */}
      <SectionLabel>{tWeather('weatherPage.stationOverview')}</SectionLabel>
      <div style={{ fontSize: 12, color: 'var(--gray)', marginBottom: 16 }}>
        Click a station on the map or in the list to explore detailed history below
      </div>
      <div style={{ display: 'flex', gap: 16, height: 340, marginBottom: 40 }}>
        <div style={{ flex: 1, borderRadius: 12, overflow: 'hidden', border: '1px solid var(--border)' }}>
          <SensorMap
            center={mapCenter}
            zoom={14}
            markers={mapMarkers}
            onMarkerClick={id => handleStationSelect(id)}
          />
        </div>
        <div style={{ width: 240, display: 'flex', flexDirection: 'column', gap: 8, overflowY: 'auto' }}>
          {stations.map(s => {
            const isSelected = s.id === selectedStationId;
            const sc = s.status === 'good' ? 'var(--green)' : s.status === 'warn' ? 'var(--amber)' : 'var(--red)';
            return (
              <div
                key={s.id}
                onClick={() => handleStationSelect(s.id)}
                style={{
                  background: isSelected ? 'var(--green-bg)' : 'var(--white)',
                  border: `1.5px solid ${isSelected ? 'var(--green)' : 'var(--border)'}`,
                  borderRadius: 10, padding: '12px 14px',
                  cursor: 'pointer', transition: 'all 0.15s', flexShrink: 0,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: sc, flexShrink: 0 }} />
                  <span style={{ fontSize: 13, fontWeight: isSelected ? 600 : 500, color: isSelected ? 'var(--green-dk)' : 'var(--dark)' }}>
                    {s.name}
                  </span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3px 10px', fontSize: 11, color: 'var(--gray)' }}>
                  <span>🌡 {fmt(s.current.temperature, '°C')}</span>
                  <span>💧 {fmt(s.current.humidity, '%')}</span>
                  <span>💨 {fmt(s.current.windSpeed, ' km/h')}</span>
                  <span>☀️ {fmt(s.current.radiation, ' W/m²')}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── 4. History Deep-Dive ────────────────────────────── */}
      <div ref={historyRef}>
        <SectionLabel>{tWeather('weatherPage.historyDeepDive')}</SectionLabel>

        {!selectedStation ? (
          <div style={{
            background: 'var(--bg)', border: '1px dashed var(--border)',
            borderRadius: 12, padding: '40px 24px', textAlign: 'center',
            color: 'var(--gray)', fontSize: 13, marginBottom: 24,
          }}>
            {tWeather('weatherPage.selectStation')}
          </div>
        ) : !isDemo ? (
          <div style={{
            background: 'var(--bg)', border: '1px dashed var(--border)',
            borderRadius: 12, padding: '40px 24px', textAlign: 'center',
            color: 'var(--gray)', fontSize: 13, marginBottom: 24,
          }}>
            Real historical data for {selectedStation.name} — coming soon.
          </div>
        ) : (
          <>
            {/* Station name + param chips */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--dark)', marginBottom: 10 }}>
                {selectedStation.name}
                <span style={{ fontSize: 11, color: 'var(--gray)', fontWeight: 400, marginLeft: 8 }}>
                  · select a parameter
                </span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {WEATHER_PARAM_CONFIG.map(p => {
                  const isActive = selectedParam === p.key;
                  return (
                    <button
                      key={p.key}
                      onClick={() => setSelectedParam(p.key as WeatherParamKey)}
                      style={{
                        padding: '5px 13px', borderRadius: 7, border: '1.5px solid',
                        borderColor: isActive ? 'var(--green)' : 'var(--border)',
                        background: isActive ? 'var(--green-bg)' : 'var(--white)',
                        color: isActive ? 'var(--green-dk)' : 'var(--gray)',
                        fontSize: 12, cursor: 'pointer', fontWeight: isActive ? 600 : 400,
                        transition: 'all 0.12s',
                      }}
                    >
                      {p.icon} {p.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Date range picker */}
            <div style={{
              display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10,
              background: 'var(--white)', border: '1px solid var(--border)',
              borderRadius: 10, padding: '10px 14px', marginBottom: 14,
            }}>
              <span style={{ fontSize: 12, color: 'var(--gray)', fontWeight: 500 }}>{tWeather('weatherPage.from')}</span>
              <input
                type="datetime-local"
                value={toLocalDatetime(startMs)}
                max={toLocalDatetime(endMs - 3_600_000)}
                onChange={e => { const v = new Date(e.target.value).getTime(); if (!isNaN(v)) setStartMs(v); }}
                style={{
                  fontSize: 12, border: '1px solid var(--border)', borderRadius: 6,
                  padding: '5px 8px', color: 'var(--dark)', background: 'var(--bg)',
                }}
              />
              <span style={{ fontSize: 12, color: 'var(--gray)', fontWeight: 500 }}>To</span>
              <input
                type="datetime-local"
                value={toLocalDatetime(endMs)}
                min={toLocalDatetime(startMs + 3_600_000)}
                max={toLocalDatetime(Date.now())}
                onChange={e => { const v = new Date(e.target.value).getTime(); if (!isNaN(v)) setEndMs(v); }}
                style={{
                  fontSize: 12, border: '1px solid var(--border)', borderRadius: 6,
                  padding: '5px 8px', color: 'var(--dark)', background: 'var(--bg)',
                }}
              />
              <div style={{ display: 'flex', gap: 4, marginLeft: 'auto' }}>
                {([
                  { label: '24h', ms: 24 * 3_600_000 },
                  { label: '7d',  ms: 7 * 24 * 3_600_000 },
                  { label: '30d', ms: 30 * 24 * 3_600_000 },
                ] as const).map(r => (
                  <button
                    key={r.label}
                    onClick={() => { const n = Date.now(); setEndMs(n); setStartMs(n - r.ms); }}
                    style={{
                      padding: '4px 11px', borderRadius: 6,
                      border: '1px solid var(--border)',
                      background: 'transparent', color: 'var(--gray)',
                      fontSize: 11, cursor: 'pointer',
                    }}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Chart */}
            <div style={{
              background: 'var(--white)', border: '1px solid var(--border)',
              borderRadius: 12, padding: '18px 20px', marginBottom: 32,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--dark)' }}>
                  {WEATHER_PARAM_CONFIG.find(p => p.key === selectedParam)?.icon}{' '}
                  {WEATHER_PARAM_CONFIG.find(p => p.key === selectedParam)?.label} — hourly
                </div>
                <div style={{ fontSize: 11, color: 'var(--gray)' }}>
                  {historyData.length} data points
                </div>
              </div>
              <HistoryChart
                data={historyData}
                unit={WEATHER_PARAM_CONFIG.find(p => p.key === selectedParam)?.unit ?? ''}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Section label ─────────────────────────────────────────────

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      fontSize: 17, fontWeight: 700, color: 'var(--dark)',
      borderLeft: '4px solid var(--green)', paddingLeft: 10,
      marginBottom: 6,
    }}>
      {children}
    </div>
  );
}

// ── Full-width history chart ───────────────────────────────────

function HistoryChart({ data, unit }: { data: Array<{ ts: string; value: number }>; unit: string }) {
  const { t: tWeather } = useTranslation();
  if (data.length < 2) {
    return (
      <div style={{ height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--gray)', fontSize: 12 }}>
        {tWeather('weatherPage.noDataRange')}
      </div>
    );
  }

  const W = 960, H = 200;
  const pad = { top: 16, right: 16, bottom: 32, left: 50 };
  const iw = W - pad.left - pad.right;
  const ih = H - pad.top - pad.bottom;

  const vals = data.map(d => d.value);
  const minV = Math.min(...vals);
  const maxV = Math.max(...vals);
  const vRange = maxV - minV || 1;

  const xs = data.map((_, i) => pad.left + (i / (data.length - 1)) * iw);
  const ys = data.map(d => pad.top + (1 - (d.value - minV) / vRange) * ih);

  const pts = xs.map((x, i) => `${x.toFixed(1)},${ys[i].toFixed(1)}`).join(' ');
  const area = `${xs[0].toFixed(1)},${pad.top + ih} ${pts} ${xs[xs.length - 1].toFixed(1)},${pad.top + ih}`;

  const step = Math.max(1, Math.floor(data.length / 7));
  const xLabels = data
    .filter((_, i) => i === 0 || i === data.length - 1 || i % step === 0)
    .map(d => {
      const idx = data.indexOf(d);
      const t = new Date(d.ts);
      const label = `${t.getMonth() + 1}/${t.getDate()} ${String(t.getHours()).padStart(2, '0')}h`;
      return { x: xs[idx], label };
    });

  const yLabels = Array.from({ length: 5 }, (_, i) => {
    const v = minV + (i / 4) * (maxV - minV);
    return {
      y: pad.top + (1 - i / 4) * ih,
      label: v.toFixed(v % 1 === 0 ? 0 : 1),
    };
  });

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" style={{ display: 'block' }}>
      {yLabels.map(({ y, label }) => (
        <g key={label}>
          <line x1={pad.left} x2={pad.left + iw} y1={y} y2={y} stroke="var(--border)" strokeWidth="0.8" strokeDasharray="4 3" />
          <text x={pad.left - 6} y={y + 4} textAnchor="end" fontSize="9.5" fill="var(--gray)">{label}</text>
        </g>
      ))}
      <text x={14} y={pad.top + ih / 2} fontSize="9.5" fill="var(--gray)"
        transform={`rotate(-90, 14, ${pad.top + ih / 2})`} textAnchor="middle">{unit}</text>
      <polygon fill="rgba(34,197,94,0.08)" points={area} />
      <polyline fill="none" stroke="var(--green)" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" points={pts} />
      {xLabels.map(({ x, label }) => (
        <text key={x} x={x} y={H - 6} textAnchor="middle" fontSize="9.5" fill="var(--gray)">{label}</text>
      ))}
    </svg>
  );
}
